import { palette, spaces } from '@kimuno/theme';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Button,
	type ButtonProps,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import useScreenSize from '../../../utils/useScreenSize';

interface DialogActionsButtonProps extends ButtonProps {
	label: string;
	loading?: boolean;
}

interface CustomDialogProps {
	dataTestId: string;
	open: boolean;
	onClose: () => void;
	title?: string;
	ariaLabel?: string;
	subTitle?: string;
	primaryButton?: DialogActionsButtonProps;
	secondaryButton?: DialogActionsButtonProps;
	backButton?: DialogActionsButtonProps;
	dialogProps?: Partial<DialogProps>;
	dialogTitleSx?: React.CSSProperties;
	dialogSubTitleSx?: React.CSSProperties;
	dialogContentSx?: React.CSSProperties;
	dialogActionsSx?: React.CSSProperties;
	footerContent?: React.ReactNode;
	children: React.ReactNode;
}

const CustomDialog: React.FC<CustomDialogProps> = (props) => {
	const {
		dataTestId,
		open,
		onClose,
		title,
		ariaLabel,
		subTitle,
		primaryButton,
		secondaryButton,
		backButton,
		dialogProps,
		dialogTitleSx,
		dialogSubTitleSx,
		dialogContentSx,
		dialogActionsSx,
		footerContent,
		children,
	} = props;
	const { isXsScreen, isSmScreen } = useScreenSize();
	const paddingSize = isXsScreen ? spaces.md : spaces.lg;
	const isMdDownScreen = isXsScreen || isSmScreen;

	const { t } = useTranslation();
	const hasDialogActions = primaryButton || secondaryButton || backButton;
	const titleId = `${dataTestId}-title`;

	return (
		<Dialog
			aria-labelledby={title ? titleId : undefined}
			aria-label={!title && ariaLabel ? ariaLabel : undefined}
			{...dialogProps}
			data-testid={dataTestId}
			fullWidth
			open={open}
			onClose={onClose}
		>
			<DialogTitle
				component="div"
				sx={{
					padding: paddingSize,
					display: 'flex',
					flexDirection: 'column',
					gap: spaces.xs,
					...dialogTitleSx,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: title ? 'space-between' : 'flex-end',
						alignItems: 'center',
					}}
				>
					{title && (
						<Typography id={titleId} variant={isXsScreen ? 'h6' : 'h5'} color={palette.text.primary}>
							{title}
						</Typography>
					)}
					<Box>
						<IconButton
							color="default"
							size="small"
							onClick={onClose}
							data-testid="close-button"
							aria-label={t('Close')}
						>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
				{subTitle && (
					<Typography variant="subtitle1" color={palette.text.secondary} sx={{ ...dialogSubTitleSx }}>
						{subTitle}
					</Typography>
				)}
			</DialogTitle>
			<DialogContent
				sx={{
					paddingY: `${paddingSize} !important`,
					paddingX: spaces.xs,
					...dialogContentSx,
				}}
			>
				{children}
			</DialogContent>
			{footerContent && (
				<Box
					sx={{
						padding: `${spaces.sm} ${paddingSize} 0`,
						borderTop: `1px solid ${palette.divider}`,
					}}
				>
					{footerContent}
				</Box>
			)}
			{hasDialogActions && (
				<DialogActions
					disableSpacing
					sx={{
						padding: paddingSize,
						display: 'flex',
						flexDirection: isMdDownScreen ? 'column-reverse' : 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: spaces.xs,
						...dialogActionsSx,
					}}
				>
					{backButton && (
						<Button
							data-testid={`${dataTestId}-third-button`}
							variant={backButton?.variant || 'text'}
							color={backButton?.color || 'inherit'}
							size="large"
							sx={{
								padding: `${spaces.xs} ${spaces.lg}`,
								width: isMdDownScreen ? '100%' : 'auto',
								whiteSpace: 'nowrap',
							}}
							onClick={backButton.onClick}
							type="button"
							aria-label={backButton.label}
							disabled={backButton.disabled}
						>
							{backButton.label}
						</Button>
					)}
					{(secondaryButton || primaryButton) && (
						<Box
							sx={{
								display: 'flex',
								gap: spaces.xs,
								justifyContent: 'flex-end',
								flexDirection: isMdDownScreen ? 'column-reverse' : 'row',
								width: '100%',
							}}
						>
							{secondaryButton && (
								<Button
									data-testid={`${dataTestId}-secondary-button`}
									onClick={secondaryButton.onClick}
									variant={secondaryButton?.variant || 'outlined'}
									color={secondaryButton?.color || 'primary'}
									size="large"
									sx={{
										padding: `${spaces.xs} ${spaces.lg}`,
										width: isMdDownScreen ? '100%' : 'auto',
									}}
									type="button"
									aria-label={secondaryButton.label}
									disabled={secondaryButton.disabled}
								>
									{secondaryButton.label}
								</Button>
							)}
							{primaryButton && (
								<Box
									sx={{
										position: 'relative',
										display: 'flex',
										alignItems: 'center',
										width: isMdDownScreen ? '100%' : 'auto',
									}}
								>
									<Button
										variant={primaryButton.variant || 'contained'}
										color={primaryButton.color || 'primary'}
										size="large"
										onClick={primaryButton.onClick}
										startIcon={primaryButton.startIcon}
										disabled={primaryButton.disabled || primaryButton.loading}
										sx={{
											padding: `${spaces.xs} ${spaces.lg}`,
											width: isMdDownScreen ? '100%' : 'auto',
										}}
										type="button"
										aria-label={primaryButton.label}
										aria-busy={primaryButton.loading}
										data-testid={`${dataTestId}-primary-button`}
									>
										{primaryButton.label}
									</Button>
									{primaryButton.loading && (
										<CircularProgress
											size={22}
											sx={{
												color: palette.primary.contrastText,
												position: 'absolute',
												top: '50%',
												left: '50%',
												transform: 'translate(-50%, -50%)!important',
												'& .MuiCircularProgress-svg': {
													width: 'auto',
													height: 'auto',
												},
											}}
										/>
									)}
								</Box>
							)}
						</Box>
					)}
				</DialogActions>
			)}
		</Dialog>
	);
};

export default CustomDialog;
